/** Account-wide consent. Always check server truth before an AI action. */
export type AiConsent = {
  granted: boolean
  current_version: string
  granted_version: string | null
  granted_at: string | null
  revoked_at: string | null
  enforcement: "off" | "log" | "on"
  providers: { name: string; purpose: string }[]
}
export type AiConsentState = {
  open: boolean
  consent: AiConsent | null
  busy: boolean
  error: string | null
}
export class ConsentCancelledError extends Error {
  constructor() { super("AI processing consent was not granted."); this.name = "ConsentCancelledError" }
}
export function consentIsCurrent(consent: AiConsent) {
  return consent.granted && consent.granted_version === consent.current_version && consent.revoked_at === null
}

export function createAiConsentController(deps: {
  userId: () => string | null
  get: () => Promise<AiConsent>
  grant: (version: string) => Promise<AiConsent>
  revoke: () => Promise<AiConsent>
}) {
  let state: AiConsentState = { open: false, consent: null, busy: false, error: null }
  let generation = 0
  let pending: Promise<boolean> | null = null
  let resolvePending: ((allowed: boolean) => void) | null = null
  const listeners = new Set<() => void>()
  function update(patch: Partial<AiConsentState>) {
    state = { ...state, ...patch }
    listeners.forEach((fn) => fn())
  }
  function finish(allowed: boolean) {
    const resolve = resolvePending
    resolvePending = null
    pending = null
    update({ open: false, busy: false })
    resolve?.(allowed)
  }
  function reset() {
    generation += 1
    finish(false)
    update({ consent: null, error: null })
  }
  async function load(forcePrompt = false) {
    const owner = deps.userId()
    const token = ++generation
    if (!owner) { reset(); return }
    update({ busy: true, error: null })
    try {
      const consent = await deps.get()
      if (token !== generation || owner !== deps.userId()) return
      update({ consent, busy: false })
      if (pending && !forcePrompt && consentIsCurrent(consent)) finish(true)
      else if (pending || forcePrompt) update({ open: true })
    } catch {
      if (token !== generation || owner !== deps.userId()) return
      update({ consent: null, busy: false, open: Boolean(pending) || forcePrompt, error: "Could not verify AI consent. Check your connection and try again." })
    }
  }
  function requireConsent(forcePrompt = false): Promise<boolean> {
    if (!deps.userId()) return Promise.resolve(false)
    if (pending) return pending
    pending = new Promise((resolve) => { resolvePending = resolve })
    const result = pending
    void load(forcePrompt)
    return result
  }
  async function grant() {
    const consent = state.consent
    const owner = deps.userId()
    if (!consent || !owner || state.busy) return
    const token = ++generation
    update({ busy: true, error: null })
    try {
      const granted = await deps.grant(consent.current_version)
      if (token !== generation || owner !== deps.userId()) return
      update({ consent: granted, busy: false })
      if (consentIsCurrent(granted)) finish(true)
      else update({ error: "Consent changed. Review the current disclosure and try again." })
    } catch (error) {
      if (token !== generation || owner !== deps.userId()) return
      update({ busy: false })
      if ((error as { code?: string })?.code === "consent_version_mismatch") {
        await load(true)
        update({ error: "The disclosure changed. Review it before agreeing again." })
      } else update({ error: "Could not save consent. Try again." })
    }
  }
  async function revoke() {
    if (!deps.userId() || state.busy) return
    reset()
    const owner = deps.userId()
    const token = ++generation
    update({ busy: true })
    try {
      const consent = await deps.revoke()
      if (token === generation && owner === deps.userId()) update({ consent, busy: false })
    } catch {
      if (token === generation && owner === deps.userId()) update({ busy: false, error: "Could not withdraw consent. Try again." })
    }
  }
  return {
    getSnapshot: () => state,
    subscribe: (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn) } },
    requireConsent, load, grant, revoke, reset,
    cancel: reset,
  }
}
